// VERSION: 3.1 - Suporte a Peso do Palete Vazio (kg)

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';
import { FiPlus, FiTrash2, FiBox, FiInfo } from 'react-icons/fi';

import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

import { getTimestamps } from '../../../utils/timestamps';
import styles from '../Parameters.module.css';

const PALLET_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-palete'
};

const BOX_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-caixa'
};

const ITEMS_PER_PAGE = 10;

const TypePalletPage = () => {
    const can = usePagePermissions('tipo_de_palete');

    // Local States
    const [editingPallet, setEditingPallet] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');

    // Pallet dimension & weight configs
    const [palletWidth, setPalletWidth] = useState('100');
    const [palletDepth, setPalletDepth] = useState('120');
    const [palletBaseHeight, setPalletBaseHeight] = useState('15');
    const [palletWeight, setPalletWeight] = useState('20'); // Empty pallet base weight (kg)

    // Linked box configs
    const [boxConfigs, setBoxConfigs] = useState([]);

    // States for adding box configuration
    const [selectedBoxTypeId, setSelectedBoxTypeId] = useState('');
    const [boxesPerPalletAir, setBoxesPerPalletAir] = useState('');
    const [boxesPerLayerAir, setBoxesPerLayerAir] = useState('');
    const [boxesPerPalletSea, setBoxesPerPalletSea] = useState('');
    const [boxesPerLayerSea, setBoxesPerLayerSea] = useState('');

    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Data Hooks
    const {
        data: palletTypes,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument
    } = useSubCollection(
        PALLET_TYPE_COLLECTION_PATH.parentCollection,
        PALLET_TYPE_COLLECTION_PATH.parentId,
        PALLET_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    const {
        data: boxTypes,
        loading: boxTypesLoading
    } = useSubCollection(
        BOX_TYPE_COLLECTION_PATH.parentCollection,
        BOX_TYPE_COLLECTION_PATH.parentId,
        BOX_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    const getBoxDimensions = useCallback((boxTypeId) => {
        const boxType = boxTypes?.find(bt => bt.id === boxTypeId);
        if (boxType) {
            return {
                height: boxType.height || 0,
                width: boxType.width || 0,
                length: boxType.length || 0
            };
        }
        return { height: 0, width: 0, length: 0 };
    }, [boxTypes]);

    const filteredPallets = useMemo(() => {
        if (!palletTypes) return [];

        let result = [...palletTypes];

        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            result = result.filter(pallet =>
                pallet.name.toLowerCase().includes(lowerCaseSearch) ||
                (pallet.boxConfigs || []).some(config => {
                    const boxType = boxTypes?.find(bt => bt.id === config.boxTypeId);
                    return boxType?.name?.toLowerCase().includes(lowerCaseSearch);
                })
            );
        }

        result.sort((a, b) => a.name.localeCompare(b.name));
        return result;
    }, [palletTypes, searchTerm, boxTypes]);

    const totalFilteredItems = filteredPallets.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentPallets = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredPallets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredPallets, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const getBoxTypeName = useCallback((boxTypeId) => {
        const boxType = boxTypes?.find(bt => bt.id === boxTypeId);
        return boxType ? boxType.name : 'Tipo não encontrado';
    }, [boxTypes]);

    const resetForm = useCallback(() => {
        setEditingPallet(null);
        setNameInput('');
        setPalletWidth('100');
        setPalletDepth('120');
        setPalletBaseHeight('15');
        setPalletWeight('20');
        setBoxConfigs([]);
        setSelectedBoxTypeId('');
        setBoxesPerPalletAir('');
        setBoxesPerLayerAir('');
        setBoxesPerPalletSea('');
        setBoxesPerLayerSea('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((pallet) => {
        setEditingPallet(pallet);
        setNameInput(pallet.name);
        setPalletWidth(pallet.palletWidth || '100');
        setPalletDepth(pallet.palletDepth || '120');
        setPalletBaseHeight(pallet.palletBaseHeight || pallet.palletHeight || '15');
        setPalletWeight(pallet.palletWeight || '20');
        setBoxConfigs(pallet.boxConfigs || []);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleAddBoxConfig = useCallback(() => {
        if (!selectedBoxTypeId) {
            setFormError('Selecione um tipo de caixa.');
            return;
        }

        if (!boxesPerPalletAir || parseFloat(boxesPerPalletAir) <= 0) {
            setFormError('Informe a quantidade de caixas por palete (via aérea).');
            return;
        }

        if (!boxesPerLayerAir || parseFloat(boxesPerLayerAir) <= 0) {
            setFormError('Informe a quantidade de caixas por camada (via aérea).');
            return;
        }

        if (!boxesPerPalletSea || parseFloat(boxesPerPalletSea) <= 0) {
            setFormError('Informe a quantidade de caixas por palete (via marítima).');
            return;
        }

        if (!boxesPerLayerSea || parseFloat(boxesPerLayerSea) <= 0) {
            setFormError('Informe a quantidade de caixas por camada (via marítima).');
            return;
        }

        if (boxConfigs.some(config => config.boxTypeId === selectedBoxTypeId)) {
            setFormError('Este tipo de caixa já foi adicionado ao palete.');
            return;
        }

        const newConfig = {
            boxTypeId: selectedBoxTypeId,
            boxesPerPalletAir: parseFloat(boxesPerPalletAir),
            boxesPerLayerAir: parseFloat(boxesPerLayerAir),
            boxesPerPalletSea: parseFloat(boxesPerPalletSea),
            boxesPerLayerSea: parseFloat(boxesPerLayerSea)
        };

        setBoxConfigs(prev => [...prev, newConfig]);
        setSelectedBoxTypeId('');
        setBoxesPerPalletAir('');
        setBoxesPerLayerAir('');
        setBoxesPerPalletSea('');
        setBoxesPerLayerSea('');
        setFormError('');
    }, [selectedBoxTypeId, boxesPerPalletAir, boxesPerLayerAir, boxesPerPalletSea, boxesPerLayerSea, boxConfigs]);

    const handleRemoveBoxConfig = useCallback((index) => {
        setBoxConfigs(prev => prev.filter((_, i) => i !== index));
    }, []);

    const validateForm = useCallback(() => {
        if (!nameInput.trim()) {
            setFormError('O nome do tipo de palete é obrigatório.');
            return false;
        }

        if (boxConfigs.length === 0) {
            setFormError('Adicione pelo menos um tipo de caixa ao palete.');
            return false;
        }

        return true;
    }, [nameInput, boxConfigs]);

    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!validateForm()) return;

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingPallet, userEmail);

        const data = {
            name: nameInput.trim(),
            palletWidth: parseFloat(palletWidth) || 100,
            palletDepth: parseFloat(palletDepth) || 120,
            palletBaseHeight: parseFloat(palletBaseHeight) || 15,
            palletWeight: parseFloat(palletWeight) || 20,
            boxConfigs,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingPallet) {
                result = await updateDocument(editingPallet.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingPallet ? 'Tipo de Palete editado com sucesso!' : 'Tipo de Palete adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingPallet ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            console.error('Erro ao salvar tipo de palete:', error);
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, palletWidth, palletDepth, palletBaseHeight, palletWeight, boxConfigs, editingPallet, validateForm, addDocument, updateDocument, handleCloseModal]);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Tipo de Palete "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredPallets.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(`Erro ao deletar tipo de palete: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar tipo de palete. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredPallets, currentPage]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingPallet ? `Editar: ${editingPallet.name}` : 'Adicionar Novo Tipo de Palete'}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingPallet ? '💾 Salvar Edição' : '✨ Adicionar Palete'}
                size="large"
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Tipo de Palete <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Palete Padrão (120x100), Palete Europeu..."
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                </div>

                <div className={styles.formGrid} style={{ marginTop: '12px' }}>
                    <div className={styles.inputGroup}>
                        <label>Largura (cm)</label>
                        <input
                            type="number"
                            value={palletWidth}
                            onChange={(e) => setPalletWidth(e.target.value)}
                            className={styles.input}
                            placeholder="100"
                            disabled={formLoading}
                            min="0"
                            step="0.1"
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Comprimento (cm)</label>
                        <input
                            type="number"
                            value={palletDepth}
                            onChange={(e) => setPalletDepth(e.target.value)}
                            className={styles.input}
                            placeholder="120"
                            disabled={formLoading}
                            min="0"
                            step="0.1"
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Altura da Madeira (cm)</label>
                        <input
                            type="number"
                            value={palletBaseHeight}
                            onChange={(e) => setPalletBaseHeight(e.target.value)}
                            className={styles.input}
                            placeholder="15"
                            disabled={formLoading}
                            min="0"
                            step="0.1"
                        />
                        <small className={styles.fieldHint}>
                            Altura do palete vazio
                        </small>
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Peso do Palete Vazio (kg)</label>
                        <input
                            type="number"
                            value={palletWeight}
                            onChange={(e) => setPalletWeight(e.target.value)}
                            className={styles.input}
                            placeholder="20"
                            disabled={formLoading}
                            min="0"
                            step="0.1"
                        />
                        <small className={styles.fieldHint}>
                            Peso da estrutura de madeira
                        </small>
                    </div>
                </div>

                <div className={styles.formSection} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiBox size={18} /></div>
                        <h3 className={styles.sectionTitle}>Tipos de Caixa Compatíveis</h3>
                        <span className={styles.sectionBadge}>{boxConfigs.length} caixa(s)</span>
                    </div>

                    {boxConfigs.length > 0 && (
                        <div className={styles.tableContainer} style={{ marginBottom: '16px', overflowX: 'auto' }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Tipo de Caixa</th>
                                        <th style={{ textAlign: 'center' }}>Aéreo</th>
                                        <th style={{ textAlign: 'center' }}>Lastro (Aéreo)</th>
                                        <th style={{ textAlign: 'center' }}>Marítimo</th>
                                        <th style={{ textAlign: 'center' }}>Lastro (Marítimo)</th>
                                        <th style={{ width: '60px', textAlign: 'center' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boxConfigs.map((config, index) => {
                                        const dims = getBoxDimensions(config.boxTypeId);
                                        return (
                                            <tr key={`${config.boxTypeId}-${index}`}>
                                                <td>
                                                    <span className={styles.badge} style={{ background: '#e3f2fd', padding: '4px 12px', borderRadius: '4px' }}>
                                                        {getBoxTypeName(config.boxTypeId)}
                                                    </span>
                                                    {dims.height > 0 && (
                                                        <span style={{ fontSize: '11px', color: '#666', marginLeft: '8px' }}>
                                                            (A: {dims.height}cm)
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                    {config.boxesPerPalletAir}
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                    {config.boxesPerLayerAir}
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                    {config.boxesPerPalletSea}
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                    {config.boxesPerLayerSea}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveBoxConfig(index)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className={styles.formGrid} style={{ marginTop: '12px', alignItems: 'flex-end' }}>
                        <div className={styles.inputGroup}>
                            <label>Tipo de Caixa <span className={styles.required}>*</span></label>
                            <select
                                value={selectedBoxTypeId}
                                onChange={(e) => setSelectedBoxTypeId(e.target.value)}
                                className={styles.select}
                                disabled={formLoading || boxTypesLoading}
                            >
                                <option value="">Selecione...</option>
                                {boxTypes?.map((type) => {
                                    const dims = getBoxDimensions(type.id);
                                    return (
                                        <option key={type.id} value={type.id}>
                                            {type.name} {dims.height > 0 ? `(A: ${dims.height}cm)` : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Cx/Palete (Aéreo) <span className={styles.required}>*</span></label>
                            <input
                                type="number"
                                value={boxesPerPalletAir}
                                onChange={(e) => setBoxesPerPalletAir(e.target.value)}
                                className={styles.input}
                                placeholder="Ex: 20"
                                min="1"
                                disabled={formLoading}
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Lastro (Aéreo) <span className={styles.required}>*</span></label>
                            <input
                                type="number"
                                value={boxesPerLayerAir}
                                onChange={(e) => setBoxesPerLayerAir(e.target.value)}
                                className={styles.input}
                                placeholder="Cx por camada"
                                min="1"
                                disabled={formLoading}
                            />
                            <small className={styles.fieldHint}>Caixas por camada (TI)</small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Cx/Palete (Marítimo) <span className={styles.required}>*</span></label>
                            <input
                                type="number"
                                value={boxesPerPalletSea}
                                onChange={(e) => setBoxesPerPalletSea(e.target.value)}
                                className={styles.input}
                                placeholder="Ex: 30"
                                min="1"
                                disabled={formLoading}
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label>Lastro (Marítimo) <span className={styles.required}>*</span></label>
                            <input
                                type="number"
                                value={boxesPerLayerSea}
                                onChange={(e) => setBoxesPerLayerSea(e.target.value)}
                                className={styles.input}
                                placeholder="Cx por camada"
                                min="1"
                                disabled={formLoading}
                            />
                            <small className={styles.fieldHint}>Caixas por camada (TI)</small>
                        </div>

                        <div className={styles.inputGroup} style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={handleAddBoxConfig}
                                className={styles.addButton}
                                disabled={formLoading || !selectedBoxTypeId}
                            >
                                <FiPlus size={18} /> Adicionar Caixa
                            </button>
                        </div>
                    </div>
                </div>

                {boxConfigs.length > 0 && (
                    <div className={styles.infoBox} style={{ marginTop: '16px', backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                        <FiInfo size={18} style={{ color: '#4caf50' }} />
                        <span>
                            <strong>Resumo:</strong> Este palete suporta <strong>{boxConfigs.length}</strong> tipo(s) de caixa cadastrado(s).
                        </span>
                    </div>
                )}
            </FormModal>

            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o tipo de palete "${deleteConfirm.name}"?`}
                isLoading={deleteLoading}
                danger={true}
            />

            <PageHeader
                title="Tipos de Palete"
                actionText="Tipo de Palete"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome do palete ou tipo de caixa..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={6} rows={ITEMS_PER_PAGE} />
                    ) : currentPallets.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📦'}
                            title={searchTerm ? 'Nenhum resultado encontrado.' : 'Nenhum tipo de palete cadastrado.'}
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome do Palete</th>
                                            <th>Dimensões (L × C × A Madeira)</th>
                                            <th>Peso Palete (Kg)</th>
                                            <th>Tipos de Caixa Compatíveis</th>
                                            <th style={{ textAlign: 'center' }}>Aéreo (Cx/Pal | Lastro)</th>
                                            <th style={{ textAlign: 'center' }}>Marítimo (Cx/Pal | Lastro)</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPallets.map((pallet) => {
                                            const configs = pallet.boxConfigs || [];
                                            const baseHeight = pallet.palletBaseHeight || pallet.palletHeight || 15;
                                            const weight = pallet.palletWeight || 20;
                                            return (
                                                <tr key={pallet.id}>
                                                    <td><strong>{pallet.name}</strong></td>
                                                    <td>
                                                        {pallet.palletWidth || 100} × {pallet.palletDepth || 120} × {baseHeight} cm
                                                    </td>
                                                    <td>
                                                        <strong>{weight} kg</strong>
                                                    </td>
                                                    <td>
                                                        {configs.length > 0 ? (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {configs.map((config, idx) => (
                                                                    <span key={idx} className={styles.badge} style={{ background: '#e3f2fd', padding: '2px 10px', borderRadius: '12px', fontSize: '12px' }}>
                                                                        {getBoxTypeName(config.boxTypeId)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#999', fontStyle: 'italic' }}>Nenhuma caixa configurada</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {configs.map(c => `${c.boxesPerPalletAir}/${c.boxesPerLayerAir}`).join(' / ') || '—'}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {configs.map(c => `${c.boxesPerPalletSea}/${c.boxesPerLayerSea}`).join(' / ') || '—'}
                                                    </td>
                                                    <td className={styles.actionsCell}>
                                                        {can.edit && (
                                                            <button onClick={() => handleEditClick(pallet)} className={styles.actionButton}>
                                                                Editar
                                                            </button>
                                                        )}
                                                        {can.delete && (
                                                            <button onClick={() => handleDeleteClick(pallet.id, pallet.name)} className={`${styles.actionButton} ${styles.deleteButton}`}>
                                                                Deletar
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="tipos de palete"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypePalletPage;